import { useLocation } from "react-router-dom";

export type Lang = "fr" | "es" | "nl" | "de" | "en";

type Dict = Record<string, string>;

const fr: Dict = {
  // Header
  "header.discuter": "Discuter",
  "header.discuter_aria": "Discuter avec Eugénie",
  "header.demande_short": "Demande",
  "header.envoyer_demande": "Envoyer ma demande",
  "header.plateforme_short": "Plateforme",
  "header.plateforme_full": "Accéder à la plateforme DEXCOM",
  "header.pill_role": ", votre conseillère ",
  "header.pill_role_suffix": "",

  // Helmet
  "seo.title": "Naboo × DEXCOM — Plateforme événementielle dédiée aux collaborateurs DEXCOM",
  "seo.description": "Plateforme Naboo réservée exclusivement aux collaborateurs DEXCOM : organisez vos séminaires, événements et team building avec des conditions négociées et un accompagnement dédié par Eugénie.",
  "seo.og.description": "Espace privé DEXCOM × Naboo : organisez vos séminaires et événements d'entreprise avec tarifs négociés et accompagnement dédié. Réservé aux équipes DEXCOM.",
  "seo.og.image_alt": "Logos DEXCOM et Naboo — Votre plateforme événementielle dédiée",
  "seo.twitter.title": "Naboo × DEXCOM — Plateforme événementielle dédiée",
  "seo.twitter.description": "Espace privé DEXCOM × Naboo : séminaires, événements et team building avec tarifs négociés et conseillère dédiée.",
  "seo.twitter.image_alt": "Naboo × DEXCOM — Plateforme événementielle dédiée",
  "seo.author": "Naboo pour DEXCOM",
  "seo.locale": "fr_FR",
  "html.lang": "fr",

  // Hero
  "hero.badge": "Quiz",
  "hero.title": "Quelques questions rapides pour trouver votre lieu idéal.",
  "hero.step.type": "Type d'événement",
  "hero.step.dates": "Dates",
  "hero.step.participants": "Participants",
  "hero.step.match": "Votre match",
  "hero.cta": "Démarrer →",
  "hero.subline": "Résultats en moins de 30 sec",

  // Stats
  "stats.partnership_value": "2025",
  "stats.partnership": "Partenaire depuis",
  "stats.events": "Nb événements",
  "stats.participants": "Nb participants",
  "stats.colleagues": "Collègues inscrits",

  // Colleagues
  "colleagues.title": "Déjà 27 collègues DEXCOM sont inscrits sur Naboo",
  "colleagues.paragraph": "Retrouvez l'historique de vos événements, le suivi de vos projets en cours et tous vos devis au même endroit.",
  "colleagues.cta": "Créer un compte →",
  "colleagues.label": "Vos collègues utilisent déjà Naboo",
  "colleagues.more": "collègues",

  // Top destinations
  "destinations.title": "Top destinations",
  "destinations.instant": "Réservation instantanée",
  "destinations.bottom_title": "Découvrez plus de 50 000 lieux et prestataires dans notre base de données",
  "destinations.bottom_subtitle": "Explorez l'ensemble du catalogue Naboo réservé aux équipes DEXCOM.",
  "destinations.bottom_cta": "Explorer les lieux →",

  // Push
  "push.line1": "Votre lieu idéal en 30 sec",
  "push.line2": "Quiz Naboo",
  "push.close": "Fermer",

  // Events
  "events.title": "Organisez tout type d'événement, de 5 à 500 participants, partout dans le monde",
  "events.prev": "Précédent",
  "events.next": "Suivant",
  "events.go_to": "Aller à l'événement",
  "events.stay": "Séjour avec nuitée",
  "events.conf": "Conférences",
  "events.rooms": "Réservation de chambres pour expositions et salons",
  "events.awayday": "Journée externe | Réunion | Session de formation",
  "events.parties": "Soirées d'entreprise | Afterworks",
  "events.meals": "Repas d'affaires | Traiteur",
  "events.tb": "Activité team building",

  // Services
  "services.title": "Une seule plateforme pour toute votre logistique",
  "services.paragraph": "De la salle de réunion au bus de transfert, on s'occupe de tout. Vous n'avez qu'un seul interlocuteur : Eugénie.",
  "services.catering": "Restauration & traiteurs",
  "services.animation": "Animation & speakers",
  "services.transport": "Transport & transferts",
  "services.hotels": "Hôtels & lieux",
  "services.coworking": "Salles & coworking",
  "services.more": "+ tout autre service événementiel sur demande",

  // Preferred terms
  "pt.title": "Bénéficiez de conditions préférentielles",
  "pt.col.invoicing": "Portage de factures",
  "pt.col.standard": "Service Standard",
  "pt.col.turnkey": "Service Clé en Main",
  "pt.group.admin": "Admin",
  "pt.group.search": "Recherche de lieu",
  "pt.group.management": "Gestion d'événement",
  "pt.row.fees": "Frais",
  "pt.row.advance": "Avance d'acompte",
  "pt.row.referencing": "Référencement de nouveaux lieux et prestataires via Naboo",
  "pt.row.autonomy": "Autonomie complète sur la plateforme",
  "pt.row.delegation": "Délégation du brief à un expert événementiel avec suggestions et support illimités",
  "pt.row.assistance": "Assistance illimitée pour négocier les prix et conditions",
  "pt.row.app": "Application événementielle pour organisateurs et participants",
  "pt.row.visit": "Visite du site",
  "pt.row.coordination": "Coordination des prestataires après signature du devis",
  "pt.row.onsite": "Gestionnaire d'événement sur place pendant l'événement (>40K€)",
  "pt.discover_fees": "Découvrez nos frais",
  "pt.cta": "Découvrir la plateforme DEXCOM",

  // How to
  "how.badge": "En 6 étapes simples",
  "how.title": "Comment faire ?",
  "how.step": "Étape",
  "how.of": "/",
  "how.prev": "Étape précédente",
  "how.next": "Étape suivante",
  "how.go_to": "Aller à l'étape",
  "how.s1.title": "Envoyez votre brief",
  "how.s1.desc": "Envoyez votre brief à votre expert événementiel (via la plateforme, par email ou par téléphone) ou recherchez en utilisant la barre de recherche en toute autonomie.",
  "how.s1.link.platform": "Lien vers la plateforme",
  "how.s2.title": "Obtenez une réponse rapide",
  "how.s2.desc": "Votre expert événementiel répond dans l'heure, fait les premières suggestions dans les 2 heures et finalise vos devis dans les 2 jours.",
  "how.s3.title": "Demandez des ajustements",
  "how.s3.desc": "Demandez des ajustements ou de nouvelles propositions. Votre expert met tout à jour dans les 24 heures jusqu'à ce que vous soyez prêt à confirmer votre réservation. Les ajustements sont illimités.",
  "how.s4.title": "Confirmez la réservation",
  "how.s4.desc": "Quand vous êtes satisfait de vos devis, téléchargez le devis qui correspond à vos attentes et lancez votre processus de demande d'achat.",
  "how.s5.title": "Créez votre demande d'achat",
  "how.s5.desc": "Pendant la validation de votre demande d'achat, cliquez sur 'Confirmer la réservation' sur la plateforme pour télécharger la facture finale et transformer votre demande d'achat en bon de commande. Naboo s'occupe de tout.",
  "how.s6.title": "Obtenez un support complet (optionnel)",
  "how.s6.desc": "Si vous avez besoin d'aide pour organiser votre événement, Naboo peut vous fournir un support complet de gestionnaire d'événement jusqu'au jour de l'événement, il suffit de demander !",

  // Footer
  "footer.terms": "Conditions générales d'utilisation",
  "footer.cgu": "CGU",
  "footer.contact": "Contactez-nous",
  "footer.cookies": "Modifier vos préférences de cookies",
  "footer.linkedin": "LinkedIn",
  "footer.instagram": "Instagram",
  "footer.email": "Email",

  // Popover
  "pop.subject": "Demande DEXCOM",
  "pop.copy": "Copier l'email",
  "pop.gmail": "Ouvrir dans Gmail",
  "pop.outlook": "Ouvrir dans Outlook",
  "pop.mail_app": "Utiliser mon application mail",
  "pop.copied": "Email copié",
  "pop.copy_error": "Impossible de copier",

  // Lang
  "lang.fr": "FR",
  "lang.es": "ES",
  "lang.nl": "NL",
  "lang.de": "DE",
  "lang.en": "EN",

  // Quiz
  "quiz.step.type": "Type",
  "quiz.step.dates": "Dates",
  "quiz.step.categories": "Catégories",
  "quiz.step.participants": "Participants",
  "quiz.step.standing": "Standing",
  "quiz.step.match": "Match",
  "quiz.title.type": "Quel type d'événement organisez-vous ?",
  "quiz.title.categories": "Quelle ambiance recherchez-vous ?",
  "quiz.title.participants": "Combien de participants ?",
  "quiz.title.standing": "Quel est votre budget ?",
  "quiz.title.dates_prefix": "Quand a lieu votre ",
  "quiz.title.dates_suffix": " ?",
  "quiz.title.event_word": "événement",
  "quiz.label.0": "Type d'événement",
  "quiz.label.1": "Dates",
  "quiz.label.2": "Catégories",
  "quiz.label.3": "Participants",
  "quiz.label.4": "Budget",
  "quiz.label.5": "Vos coordonnées",
  "quiz.month.0": "Janvier",
  "quiz.month.1": "Février",
  "quiz.month.2": "Mars",
  "quiz.month.3": "Avril",
  "quiz.month.4": "Mai",
  "quiz.month.5": "Juin",
  "quiz.month.6": "Juillet",
  "quiz.month.7": "Août",
  "quiz.month.8": "Septembre",
  "quiz.month.9": "Octobre",
  "quiz.month.10": "Novembre",
  "quiz.month.11": "Décembre",
  "quiz.evt.seminaire": "Séminaire d'entreprise",
  "quiz.evt.journee_hors_site": "Journée hors site",
  "quiz.evt.soiree": "Soirée d'entreprise",
  "quiz.evt.team_building": "Activité team building",
  "quiz.evt.conference": "Conférence",
  "quiz.evt.lancement_produit": "Lancement de produit",
  "quiz.evt.autre": "Autre",
  "quiz.evt.placeholder": "Précisez le type d'événement",
  "quiz.cat.Plage": "Plage",
  "quiz.cat.Montagne": "Montagne",
  "quiz.cat.Ville": "Ville",
  "quiz.cat.Nature": "Nature",
  "quiz.cat.Désert": "Désert",
  "quiz.cat.Tropical": "Tropical",
  "quiz.cat.Bord de lac": "Bord de lac",
  "quiz.cat.Île": "Île",
  "quiz.cat.Historique": "Historique",
  "quiz.cat.Luxe": "Luxe",
  "quiz.cat.Aventure": "Aventure",
  "quiz.cat.Bien-être": "Bien-être",
  "quiz.dates.placeholder": "Choisir une date",
  "quiz.dates.or_month": "Ou choisissez un mois",
  "quiz.participants.hint": "Nombre de participants",
  "quiz.participants.placeholder": "Ex : 25",
  "quiz.budget.per_person": "soit {p} € par personne",
  "quiz.budget.or": "OU",
  "quiz.budget.placeholder": "Budget total exact",
  "quiz.budget.exact": "Saisir un montant exact",
  "quiz.standing.Essentiel": "Essentiel",
  "quiz.standing.Confort": "Confort",
  "quiz.standing.Premium": "Premium",
  "quiz.standing.Prestige": "Prestige",
  "quiz.standing.Exception": "Exception",
  "quiz.match.title": "Recevez vos suggestions",
  "quiz.match.subtitle": "Indiquez votre email professionnel pour recevoir votre sélection personnalisée.",
  "quiz.match.email_placeholder": "votre.email@dexcom.com",
  "quiz.match.cta": "Recevoir mes suggestions",
  "quiz.match.sending": "Envoi en cours…",
  "quiz.match_suffix": "% de match",
  "quiz.email.required": "Email requis",
  "quiz.email.invalid": "Email invalide",
  "quiz.email.pro": "Merci d'utiliser votre email professionnel",
  "quiz.success.title": "Demande envoyée !",
  "quiz.success.subtitle": "Eugénie revient vers vous très rapidement avec une sélection personnalisée.",
  "quiz.success.close": "Fermer",
  "quiz.nav.back": "Retour",
  "quiz.nav.next": "Suivant",
  "quiz.aria.close": "Fermer",
  "quiz.tier.Petit comité": "Petit comité",
  "quiz.tier.Groupe convivial": "Groupe convivial",
  "quiz.tier.Grande équipe": "Grande équipe",
  "quiz.tier.Grand événement": "Grand événement",
  "quiz.tier.Événement XL": "Événement XL",
};

const es: Dict = {
  ...fr,
  "header.discuter": "Conversar",
  "header.discuter_aria": "Conversar con Eugénie",
  "header.demande_short": "Solicitud",
  "header.envoyer_demande": "Enviar mi solicitud",
  "header.plateforme_short": "Plataforma",
  "header.plateforme_full": "Acceder a la plataforma DEXCOM",
  "header.pill_role": ", su asesora ",
  "header.pill_role_suffix": "",

  "seo.title": "Naboo × DEXCOM — Plataforma de eventos dedicada a los colaboradores de DEXCOM",
  "seo.description": "Plataforma Naboo reservada exclusivamente a los colaboradores de DEXCOM: organice sus seminarios, eventos y team building con condiciones negociadas y un acompañamiento dedicado por Eugénie.",
  "seo.og.description": "Espacio privado DEXCOM × Naboo: organice sus seminarios y eventos con tarifas negociadas y acompañamiento dedicado.",
  "seo.og.image_alt": "Logos DEXCOM y Naboo — Su plataforma de eventos dedicada",
  "seo.twitter.title": "Naboo × DEXCOM — Plataforma de eventos dedicada",
  "seo.twitter.description": "Espacio privado DEXCOM × Naboo: seminarios, eventos y team building con tarifas negociadas y asesora dedicada.",
  "seo.twitter.image_alt": "Naboo × DEXCOM — Plataforma de eventos dedicada",
  "seo.author": "Naboo para DEXCOM",
  "seo.locale": "es_ES",
  "html.lang": "es",

  "hero.badge": "Quiz",
  "hero.title": "Algunas preguntas rápidas para encontrar su lugar ideal.",
  "hero.step.type": "Tipo de evento",
  "hero.step.dates": "Fechas",
  "hero.step.participants": "Participantes",
  "hero.step.match": "Su match",
  "hero.cta": "Empezar →",
  "hero.subline": "Obtén resultados en menos de 30 seg",

  "stats.partnership_value": "2025",
  "stats.partnership": "Socio desde",
  "stats.events": "Nº de eventos",
  "stats.participants": "Nº de participantes",
  "stats.colleagues": "Colaboradores registrados",

  "colleagues.title": "Ya hay 27 colaboradores de DEXCOM registrados en Naboo",
  "colleagues.paragraph": "Encuentre el historial de sus eventos, el seguimiento de sus proyectos en curso y todos sus presupuestos en un mismo lugar.",
  "colleagues.cta": "Crear una cuenta →",
  "colleagues.label": "Sus colaboradores ya utilizan Naboo",
  "colleagues.more": "colaboradores",

  "destinations.title": "Destinos destacados",
  "destinations.instant": "Reserva instantánea",
  "destinations.bottom_title": "Descubra más de 50 000 lugares y proveedores en nuestra base de datos",
  "destinations.bottom_subtitle": "Explore todo el catálogo Naboo reservado a los equipos de DEXCOM.",
  "destinations.bottom_cta": "Explorar los lugares →",

  "push.line1": "Su lugar ideal en 30 seg",
  "push.line2": "Quiz Naboo",
  "push.close": "Cerrar",

  "events.title": "Organice cualquier tipo de evento, de 5 a 500 participantes, en cualquier lugar del mundo",
  "events.prev": "Anterior",
  "events.next": "Siguiente",
  "events.go_to": "Ir al evento",
  "events.stay": "Estancia con noche",
  "events.conf": "Conferencias",
  "events.rooms": "Reserva de habitaciones para exposiciones y ferias",
  "events.awayday": "Jornada externa | Reunión | Sesión de formación",
  "events.parties": "Fiestas de empresa | Afterworks",
  "events.meals": "Comidas de negocios | Catering",
  "events.tb": "Actividad de team building",

  "services.title": "Una sola plataforma para toda su logística",
  "services.paragraph": "De la sala de reuniones al bus de transferencia, nos ocupamos de todo. Solo tiene un interlocutor: Eugénie.",
  "services.catering": "Restauración y catering",
  "services.animation": "Animación y ponentes",
  "services.transport": "Transporte y transferencias",
  "services.hotels": "Hoteles y lugares",
  "services.coworking": "Salas y coworking",
  "services.more": "+ cualquier otro servicio de eventos a petición",

  "pt.title": "Benefíciese de condiciones preferentes",
  "pt.col.invoicing": "Gestión de facturas",
  "pt.col.standard": "Servicio Estándar",
  "pt.col.turnkey": "Servicio Llave en Mano",
  "pt.group.admin": "Admin",
  "pt.group.search": "Búsqueda de lugar",
  "pt.group.management": "Gestión de eventos",
  "pt.row.fees": "Tarifas",
  "pt.row.advance": "Anticipo",
  "pt.row.referencing": "Referenciación de nuevos lugares y proveedores vía Naboo",
  "pt.row.autonomy": "Autonomía completa en la plataforma",
  "pt.row.delegation": "Delegación del brief a un experto en eventos con sugerencias y soporte ilimitados",
  "pt.row.assistance": "Asistencia ilimitada para negociar precios y condiciones",
  "pt.row.app": "Aplicación de eventos para organizadores y participantes",
  "pt.row.visit": "Visita al lugar",
  "pt.row.coordination": "Coordinación de los proveedores tras la firma del presupuesto",
  "pt.row.onsite": "Gestor de eventos in situ durante el evento (>40K€)",
  "pt.discover_fees": "Descubra nuestras tarifas",
  "pt.cta": "Descubrir la plataforma DEXCOM",

  "how.badge": "En 6 pasos simples",
  "how.title": "¿Cómo funciona?",
  "how.step": "Paso",
  "how.prev": "Paso anterior",
  "how.next": "Paso siguiente",
  "how.go_to": "Ir al paso",
  "how.s1.title": "Envíe su brief",
  "how.s1.desc": "Envíe su brief a su experto en eventos (vía la plataforma, por email o por teléfono) o busque en total autonomía con la barra de búsqueda.",
  "how.s1.link.platform": "Enlace a la plataforma",
  "how.s2.title": "Obtenga una respuesta rápida",
  "how.s2.desc": "Su experto en eventos responde en una hora, hace las primeras sugerencias en 2 horas y finaliza sus presupuestos en 2 días.",
  "how.s3.title": "Solicite ajustes",
  "how.s3.desc": "Solicite ajustes o nuevas propuestas. Su experto lo actualiza todo en 24 horas hasta que esté listo para confirmar su reserva.",
  "how.s4.title": "Confirme la reserva",
  "how.s4.desc": "Cuando esté satisfecho con sus presupuestos, descargue el que corresponda a sus expectativas y lance su orden de compra.",
  "how.s5.title": "Cree su orden de compra",
  "how.s5.desc": "Durante la validación de su orden de compra, haga clic en 'Confirmar la reserva' en la plataforma para descargar la factura final.",
  "how.s6.title": "Obtenga un soporte completo (opcional)",
  "how.s6.desc": "Si necesita ayuda para organizar su evento, Naboo puede ofrecerle un soporte completo hasta el día del evento.",

  "footer.terms": "Términos y condiciones de uso",
  "footer.contact": "Contáctenos",
  "footer.cookies": "Modificar sus preferencias de cookies",

  "pop.subject": "Solicitud DEXCOM",
  "pop.copy": "Copiar el email",
  "pop.gmail": "Abrir en Gmail",
  "pop.outlook": "Abrir en Outlook",
  "pop.mail_app": "Usar mi aplicación de correo",
  "pop.copied": "Email copiado",
  "pop.copy_error": "Imposible copiar",

  // Quiz ES
  "quiz.step.type": "Tipo",
  "quiz.step.dates": "Fechas",
  "quiz.step.categories": "Categorías",
  "quiz.step.participants": "Participantes",
  "quiz.step.standing": "Categoría",
  "quiz.step.match": "Match",
  "quiz.title.type": "¿Qué tipo de evento organiza?",
  "quiz.title.categories": "¿Qué ambiente busca?",
  "quiz.title.participants": "¿Cuántos participantes?",
  "quiz.title.standing": "¿Cuál es su presupuesto?",
  "quiz.title.dates_prefix": "¿Cuándo es su ",
  "quiz.title.dates_suffix": "?",
  "quiz.title.event_word": "evento",
  "quiz.label.0": "Tipo de evento",
  "quiz.label.1": "Fechas",
  "quiz.label.2": "Categorías",
  "quiz.label.3": "Participantes",
  "quiz.label.4": "Presupuesto",
  "quiz.label.5": "Sus datos",
  "quiz.month.0": "Enero","quiz.month.1": "Febrero","quiz.month.2": "Marzo","quiz.month.3": "Abril","quiz.month.4": "Mayo","quiz.month.5": "Junio","quiz.month.6": "Julio","quiz.month.7": "Agosto","quiz.month.8": "Septiembre","quiz.month.9": "Octubre","quiz.month.10": "Noviembre","quiz.month.11": "Diciembre",
  "quiz.evt.seminaire": "Seminario de empresa",
  "quiz.evt.journee_hors_site": "Jornada externa",
  "quiz.evt.soiree": "Fiesta de empresa",
  "quiz.evt.team_building": "Actividad de team building",
  "quiz.evt.conference": "Conferencia",
  "quiz.evt.lancement_produit": "Lanzamiento de producto",
  "quiz.evt.autre": "Otro",
  "quiz.evt.placeholder": "Indique el tipo de evento",
  "quiz.cat.Plage": "Playa","quiz.cat.Montagne": "Montaña","quiz.cat.Ville": "Ciudad","quiz.cat.Nature": "Naturaleza","quiz.cat.Désert": "Desierto","quiz.cat.Tropical": "Tropical","quiz.cat.Bord de lac": "Orilla del lago","quiz.cat.Île": "Isla","quiz.cat.Historique": "Histórico","quiz.cat.Luxe": "Lujo","quiz.cat.Aventure": "Aventura","quiz.cat.Bien-être": "Bienestar",
  "quiz.dates.placeholder": "Elegir una fecha",
  "quiz.dates.or_month": "O elija un mes",
  "quiz.participants.hint": "Número de participantes",
  "quiz.participants.placeholder": "Ej: 25",
  "quiz.budget.per_person": "es decir {p} € por persona",
  "quiz.budget.or": "O",
  "quiz.budget.placeholder": "Presupuesto total exacto",
  "quiz.budget.exact": "Introducir un importe exacto",
  "quiz.standing.Essentiel": "Esencial","quiz.standing.Confort": "Confort","quiz.standing.Premium": "Premium","quiz.standing.Prestige": "Prestigio","quiz.standing.Exception": "Excepción",
  "quiz.match.title": "Reciba sus sugerencias",
  "quiz.match.subtitle": "Indique su email profesional para recibir su selección personalizada.",
  "quiz.match.email_placeholder": "su.email@dexcom.com",
  "quiz.match.cta": "Recibir mis sugerencias",
  "quiz.match.sending": "Enviando…",
  "quiz.match_suffix": "% de match",
  "quiz.email.required": "Email requerido",
  "quiz.email.invalid": "Email inválido",
  "quiz.email.pro": "Por favor, use su email profesional",
  "quiz.success.title": "¡Solicitud enviada!",
  "quiz.success.subtitle": "Eugénie se pondrá en contacto con usted muy pronto con una selección personalizada.",
  "quiz.success.close": "Cerrar",
  "quiz.nav.back": "Atrás",
  "quiz.nav.next": "Siguiente",
  "quiz.aria.close": "Cerrar",
  "quiz.tier.Petit comité": "Comité pequeño",
  "quiz.tier.Groupe convivial": "Grupo reducido",
  "quiz.tier.Grande équipe": "Equipo grande",
  "quiz.tier.Grand événement": "Gran evento",
  "quiz.tier.Événement XL": "Evento XL",
};

const en: Dict = {
  ...fr,
  "header.discuter": "Chat",
  "header.discuter_aria": "Chat with Eugénie",
  "header.demande_short": "Request",
  "header.envoyer_demande": "Send my request",
  "header.plateforme_short": "Platform",
  "header.plateforme_full": "Go to the DEXCOM platform",
  "header.pill_role": ", your ",
  "header.pill_role_suffix": " advisor",

  "seo.title": "Naboo × DEXCOM — Event platform dedicated to DEXCOM employees",
  "seo.description": "Naboo platform reserved exclusively to DEXCOM employees: organise your seminars, events and team building with negotiated terms and dedicated support by Eugénie.",
  "seo.og.description": "Private DEXCOM × Naboo space: organise corporate events with negotiated rates and dedicated support.",
  "seo.og.image_alt": "DEXCOM and Naboo logos — Your dedicated event platform",
  "seo.twitter.title": "Naboo × DEXCOM — Dedicated event platform",
  "seo.twitter.description": "Private DEXCOM × Naboo space: seminars, events and team building with negotiated rates and a dedicated advisor.",
  "seo.twitter.image_alt": "Naboo × DEXCOM — Dedicated event platform",
  "seo.author": "Naboo for DEXCOM",
  "seo.locale": "en_GB",
  "html.lang": "en",

  "hero.title": "A few quick questions to find your ideal venue.",
  "hero.step.type": "Event type",
  "hero.step.dates": "Dates",
  "hero.step.participants": "Participants",
  "hero.step.match": "Your match",
  "hero.cta": "Start →",
  "hero.subline": "Results in under 30 sec",

  "stats.partnership_value": "2025",
  "stats.partnership": "Partner since",
  "stats.events": "Events",
  "stats.participants": "Participants",
  "stats.colleagues": "Registered colleagues",

  "colleagues.title": "27 DEXCOM colleagues are already on Naboo",
  "colleagues.paragraph": "Find the history of your events, the follow-up of your ongoing projects and all your quotes in one place.",
  "colleagues.cta": "Create an account →",
  "colleagues.label": "Your colleagues already use Naboo",
  "colleagues.more": "colleagues",

  "destinations.title": "Top destinations",
  "destinations.instant": "Instant booking",
  "destinations.bottom_title": "Discover over 50,000 venues and service providers in our database",
  "destinations.bottom_subtitle": "Explore the full Naboo catalogue reserved for DEXCOM teams.",
  "destinations.bottom_cta": "Explore venues →",

  "push.line1": "Your ideal venue in 30 sec",
  "push.line2": "Naboo Quiz",
  "push.close": "Close",

  "events.title": "Organise any type of event, from 5 to 500 participants, anywhere in the world",
  "events.prev": "Previous",
  "events.next": "Next",
  "events.go_to": "Go to event",
  "events.stay": "Overnight retreat",
  "events.conf": "Conferences",
  "events.rooms": "Exhibitions | Fairs",
  "events.awayday": "Away-day | Meeting | Training session",
  "events.parties": "Corporate parties | Afterworks",
  "events.meals": "Business meals | Catering",
  "events.tb": "Team building activity",

  "services.title": "One platform for all your logistics",
  "services.paragraph": "From meeting rooms to bus transfers, we handle everything. You only have one contact: Eugénie.",
  "services.catering": "Catering & restaurants",
  "services.animation": "Speakers & entertainment",
  "services.transport": "Transport & transfers",
  "services.hotels": "Hotels & venues",
  "services.coworking": "Meeting rooms & coworking",
  "services.more": "+ any other event service on demand",

  "pt.title": "Benefit from preferential rates",
  "pt.col.invoicing": "Invoice carrying",
  "pt.col.standard": "Standard Service",
  "pt.col.turnkey": "Turnkey Service",
  "pt.group.admin": "Admin",
  "pt.group.search": "Venue finding",
  "pt.group.management": "Event management",
  "pt.row.fees": "Fees",
  "pt.row.advance": "Deposit advance payment",
  "pt.row.referencing": "Listing of new venues and providers via Naboo",
  "pt.row.autonomy": "Full autonomy on the platform",
  "pt.row.delegation": "Brief delegation to event expert with unlimited suggestions and support",
  "pt.row.assistance": "Unlimited assistance to negotiate prices and T&Cs",
  "pt.row.app": "Event app for organisers and participants",
  "pt.row.visit": "Site visit",
  "pt.row.coordination": "Coordination of providers after signing the quote",
  "pt.row.onsite": "On-site event manager during the event (>€40K)",
  "pt.discover_fees": "Discover our fees",
  "pt.cta": "Discover the DEXCOM platform",

  "how.badge": "In 6 simple steps",
  "how.title": "How does it work?",
  "how.step": "Step",
  "how.prev": "Previous step",
  "how.next": "Next step",
  "how.go_to": "Go to step",
  "how.s1.title": "Send your brief",
  "how.s1.desc": "Send your brief to your event expert (via the platform, email or phone) or search using the search bar in full autonomy.",
  "how.s1.link.platform": "Link to the platform",
  "how.s2.title": "Get a prompt answer",
  "how.s2.desc": "Your event expert answers within 1 hour, makes first suggestions within 2 hours, and finalises quotes within 2 days.",
  "how.s3.title": "Ask for adjustments",
  "how.s3.desc": "Ask for adjustments or new proposals. Your expert updates everything within 24 hours until you are ready to confirm. Adjustments are unlimited.",
  "how.s4.title": "Confirm booking",
  "how.s4.desc": "When you are happy with your quotes, download the one that meets your expectations and start your purchase request.",
  "how.s5.title": "Create your purchase request",
  "how.s5.desc": "While your PR is being validated, click 'Confirm booking' on the platform to download the final invoice and transform it into a PO. Naboo handles the payment.",
  "how.s6.title": "Get full support (optional)",
  "how.s6.desc": "If you need help organising your event, Naboo can provide full event manager support up until the day of the event — just ask!",

  "footer.terms": "General terms of use",
  "footer.cgu": "T&Cs",
  "footer.contact": "Contact us",
  "footer.cookies": "Change your cookie preferences",

  "pop.subject": "DEXCOM request",
  "pop.copy": "Copy email",
  "pop.gmail": "Open in Gmail",
  "pop.outlook": "Open in Outlook",
  "pop.mail_app": "Use my mail app",
  "pop.copied": "Email copied",
  "pop.copy_error": "Unable to copy",

  // Quiz EN
  "quiz.step.type": "Type",
  "quiz.step.dates": "Dates",
  "quiz.step.categories": "Categories",
  "quiz.step.participants": "Participants",
  "quiz.step.standing": "Standing",
  "quiz.step.match": "Match",
  "quiz.title.type": "What type of event are you organising?",
  "quiz.title.categories": "What atmosphere are you looking for?",
  "quiz.title.participants": "How many participants?",
  "quiz.title.standing": "What is your budget?",
  "quiz.title.dates_prefix": "When is your ",
  "quiz.title.dates_suffix": "?",
  "quiz.title.event_word": "event",
  "quiz.label.0": "Event type","quiz.label.1": "Dates","quiz.label.2": "Categories","quiz.label.3": "Participants","quiz.label.4": "Budget","quiz.label.5": "Your details",
  "quiz.month.0": "January","quiz.month.1": "February","quiz.month.2": "March","quiz.month.3": "April","quiz.month.4": "May","quiz.month.5": "June","quiz.month.6": "July","quiz.month.7": "August","quiz.month.8": "September","quiz.month.9": "October","quiz.month.10": "November","quiz.month.11": "December",
  "quiz.evt.seminaire": "Corporate seminar",
  "quiz.evt.journee_hors_site": "Offsite day",
  "quiz.evt.soiree": "Corporate party",
  "quiz.evt.team_building": "Team building activity",
  "quiz.evt.conference": "Conference",
  "quiz.evt.lancement_produit": "Product launch",
  "quiz.evt.autre": "Other",
  "quiz.evt.placeholder": "Specify the event type",
  "quiz.cat.Plage": "Beach","quiz.cat.Montagne": "Mountain","quiz.cat.Ville": "City","quiz.cat.Nature": "Nature","quiz.cat.Désert": "Desert","quiz.cat.Tropical": "Tropical","quiz.cat.Bord de lac": "Lakeside","quiz.cat.Île": "Island","quiz.cat.Historique": "Historic","quiz.cat.Luxe": "Luxury","quiz.cat.Aventure": "Adventure","quiz.cat.Bien-être": "Wellness",
  "quiz.dates.placeholder": "Pick a date",
  "quiz.dates.or_month": "Or pick a month",
  "quiz.participants.hint": "Number of participants",
  "quiz.participants.placeholder": "E.g. 25",
  "quiz.budget.per_person": "i.e. €{p} per person",
  "quiz.budget.or": "OR",
  "quiz.budget.placeholder": "Exact total budget",
  "quiz.budget.exact": "Enter an exact amount",
  "quiz.standing.Essentiel": "Essential","quiz.standing.Confort": "Comfort","quiz.standing.Premium": "Premium","quiz.standing.Prestige": "Prestige","quiz.standing.Exception": "Exceptional",
  "quiz.match.title": "Receive your suggestions",
  "quiz.match.subtitle": "Enter your work email to receive your personalised selection.",
  "quiz.match.email_placeholder": "your.email@dexcom.com",
  "quiz.match.cta": "Get my suggestions",
  "quiz.match.sending": "Sending…",
  "quiz.match_suffix": "% match",
  "quiz.email.required": "Email required",
  "quiz.email.invalid": "Invalid email",
  "quiz.email.pro": "Please use your work email",
  "quiz.success.title": "Request sent!",
  "quiz.success.subtitle": "Eugénie will get back to you very shortly with a personalised selection.",
  "quiz.success.close": "Close",
  "quiz.nav.back": "Back",
  "quiz.nav.next": "Next",
  "quiz.aria.close": "Close",
  "quiz.tier.Petit comité": "Small group",
  "quiz.tier.Groupe convivial": "Friendly group",
  "quiz.tier.Grande équipe": "Large team",
  "quiz.tier.Grand événement": "Large event",
  "quiz.tier.Événement XL": "XL event",
};

const nl: Dict = {
  ...en,
  "header.discuter": "Chatten",
  "header.discuter_aria": "Chatten met Eugénie",
  "header.demande_short": "Aanvraag",
  "header.envoyer_demande": "Mijn aanvraag versturen",
  "header.plateforme_short": "Platform",
  "header.plateforme_full": "Naar het DEXCOM-platform",
  "header.pill_role": ", uw adviseur ",
  "header.pill_role_suffix": "",

  "seo.title": "Naboo × DEXCOM — Eventplatform voor DEXCOM-medewerkers",
  "seo.description": "Naboo-platform exclusief voor DEXCOM-medewerkers: organiseer uw seminars, evenementen en teambuilding met onderhandelde voorwaarden en persoonlijke ondersteuning door Eugénie.",
  "seo.og.description": "Privé DEXCOM × Naboo-omgeving: organiseer bedrijfsevenementen met onderhandelde tarieven en persoonlijke ondersteuning.",
  "seo.og.image_alt": "DEXCOM- en Naboo-logo's — Uw eventplatform",
  "seo.twitter.title": "Naboo × DEXCOM — Eventplatform",
  "seo.twitter.description": "Privé DEXCOM × Naboo-omgeving: seminars, evenementen en teambuilding met onderhandelde tarieven.",
  "seo.twitter.image_alt": "Naboo × DEXCOM — Eventplatform",
  "seo.author": "Naboo voor DEXCOM",
  "seo.locale": "nl_NL",
  "html.lang": "nl",

  "hero.title": "Een paar snelle vragen om uw ideale locatie te vinden.",
  "hero.step.type": "Evenementtype",
  "hero.step.dates": "Data",
  "hero.step.participants": "Deelnemers",
  "hero.step.match": "Uw match",
  "hero.cta": "Starten →",
  "hero.subline": "Resultaten in minder dan 30 sec",

  "stats.partnership_value": "2025",
  "stats.partnership": "Partner sinds",
  "stats.events": "Evenementen",
  "stats.participants": "Deelnemers",
  "stats.colleagues": "Geregistreerde collega's",

  "colleagues.title": "Al 27 DEXCOM-collega's gebruiken Naboo",
  "colleagues.paragraph": "Vind de geschiedenis van uw evenementen, de opvolging van uw lopende projecten en al uw offertes op één plek.",
  "colleagues.cta": "Account aanmaken →",
  "colleagues.label": "Uw collega's gebruiken Naboo al",
  "colleagues.more": "collega's",

  "destinations.title": "Top bestemmingen",
  "destinations.instant": "Directe boeking",
  "destinations.bottom_title": "Ontdek meer dan 50000 locaties en dienstverleners in onze database",
  "destinations.bottom_subtitle": "Ontdek de volledige Naboo-catalogus voor DEXCOM-teams.",
  "destinations.bottom_cta": "Locaties verkennen →",

  "push.line1": "Uw ideale locatie in 30 sec",
  "push.line2": "Naboo Quiz",
  "push.close": "Sluiten",

  "events.title": "Organiseer elk type evenement, van 5 tot 500 deelnemers, overal ter wereld",
  "events.stay": "Verblijf met overnachting",
  "events.conf": "Conferenties",
  "events.rooms": "Kamerblokreserveringen voor beurzen",
  "events.awayday": "Externe dag | Vergadering | Training",
  "events.parties": "Bedrijfsfeesten | Afterworks",
  "events.meals": "Zakelijke maaltijden | Catering",
  "events.tb": "Teambuilding",

  "services.title": "Eén platform voor al uw logistiek",
  "services.paragraph": "Van vergaderzaal tot transferbus, wij regelen alles. U heeft slechts één contactpersoon: Eugénie.",
  "services.catering": "Catering & restaurants",
  "services.animation": "Sprekers & entertainment",
  "services.transport": "Transport & transfers",
  "services.hotels": "Hotels & locaties",
  "services.coworking": "Vergaderruimtes & coworking",
  "services.more": "+ elke andere eventdienst op aanvraag",

  "pt.title": "Profiteer van voorkeurscondities",
  "pt.col.invoicing": "Factuurdrager",
  "pt.col.standard": "Standaard Service",
  "pt.col.turnkey": "Sleutelklare Service",
  "pt.group.admin": "Beheer",
  "pt.group.search": "Locatie zoeken",
  "pt.group.management": "Evenementenbeheer",
  "pt.row.fees": "Kosten",
  "pt.row.advance": "Vooruitbetaling borg",
  "pt.row.referencing": "Vermelding van nieuwe locaties en dienstverleners via Naboo",
  "pt.row.autonomy": "Volledige autonomie op het platform",
  "pt.row.delegation": "Onbeperkte gepersonaliseerde suggesties en ondersteuning van evenementenexpert",
  "pt.row.assistance": "Onbeperkte assistentie bij het onderhandelen over prijzen en voorwaarden",
  "pt.row.app": "App voor organisator en deelnemers",
  "pt.row.visit": "Locatiebezoek",
  "pt.row.coordination": "Coördinatie van dienstverleners na ondertekening van de offerte",
  "pt.row.onsite": "Evenementenmanager ter plaatse tijdens de gebeurtenis (>40K€)",
  "pt.discover_fees": "Ontdek onze tarieven",
  "pt.cta": "Ontdek het DEXCOM-platform",

  "how.badge": "In 6 eenvoudige stappen",
  "how.title": "Hoe werkt het?",
  "how.step": "Stap",
  "how.prev": "Vorige stap",
  "how.next": "Volgende stap",
  "how.go_to": "Ga naar stap",
  "how.s1.title": "Stuur uw briefing",
  "how.s1.desc": "Stuur uw briefing naar uw evenementexpert (via het platform, e-mail of telefoon) of zoek via de zoekbalk volledig zelfstandig.",
  "how.s1.link.platform": "Link naar het platform",
  "how.s2.title": "Krijg een snel antwoord",
  "how.s2.desc": "Uw evenementexpert reageert binnen 1 uur, doet de eerste suggesties binnen 2 uur en maakt uw offertes binnen 2 dagen af.",
  "how.s3.title": "Vraag om aanpassingen",
  "how.s3.desc": "Vraag om aanpassingen of nieuwe voorstellen. Uw expert werkt alles binnen 24 uur bij totdat u klaar bent om uw boeking te bevestigen. Aanpassingen zijn onbeperkt.",
  "how.s4.title": "Maak uw inkoopverzoek",
  "how.s4.desc": "Wanneer u tevreden bent met de ontvangen offertes, kunt u diegene downloaden die aan uw verwachtingen voldoet en het proces van inkoopverzoek starten.",
  "how.s5.title": "Bevestig de boeking",
  "how.s5.desc": "Terwijl uw verzoek wordt gevalideerd, klikt u op 'Boeking bevestigen' op het platform om de definitieve factuur te downloaden en het inkoopverzoek om te zetten in een inkooporder. Maakt u zich geen zorgen over de betaling – Naboo regelt alles.",
  "how.s6.title": "Krijg volledige ondersteuning (optioneel)",
  "how.s6.desc": "Mocht u hulp nodig hebben bij het organiseren van uw evenement, dan kan Naboo u voorzien van volledige ondersteuning door een evenementenmanager, tot aan de dag van het evenement. U hoeft het alleen te vragen!",

  "footer.terms": "Algemene gebruiksvoorwaarden",
  "footer.contact": "Neem contact op",
  "footer.cookies": "Wijzig uw cookievoorkeuren",

  "pop.subject": "DEXCOM aanvraag",
  "pop.copy": "E-mail kopiëren",
  "pop.gmail": "Openen in Gmail",
  "pop.outlook": "Openen in Outlook",
  "pop.mail_app": "Mijn e-mailapp gebruiken",
  "pop.copied": "E-mail gekopieerd",
  "pop.copy_error": "Kopiëren mislukt",

  // Quiz NL
  "quiz.step.type": "Type",
  "quiz.step.dates": "Data",
  "quiz.step.categories": "Categorieën",
  "quiz.step.participants": "Deelnemers",
  "quiz.step.standing": "Niveau",
  "quiz.step.match": "Match",
  "quiz.title.type": "Welk type evenement organiseert u?",
  "quiz.title.categories": "Welke sfeer zoekt u?",
  "quiz.title.participants": "Hoeveel deelnemers?",
  "quiz.title.standing": "Wat is uw budget?",
  "quiz.title.dates_prefix": "Wanneer vindt uw ",
  "quiz.title.dates_suffix": " plaats?",
  "quiz.title.event_word": "evenement",
  "quiz.label.0": "Type evenement","quiz.label.1": "Data","quiz.label.2": "Categorieën","quiz.label.3": "Deelnemers","quiz.label.4": "Budget","quiz.label.5": "Uw gegevens",
  "quiz.month.0": "Januari","quiz.month.1": "Februari","quiz.month.2": "Maart","quiz.month.3": "April","quiz.month.4": "Mei","quiz.month.5": "Juni","quiz.month.6": "Juli","quiz.month.7": "Augustus","quiz.month.8": "September","quiz.month.9": "Oktober","quiz.month.10": "November","quiz.month.11": "December",
  "quiz.evt.seminaire": "Bedrijfsseminar",
  "quiz.evt.journee_hors_site": "Offsite dag",
  "quiz.evt.soiree": "Bedrijfsfeest",
  "quiz.evt.team_building": "Teambuilding-activiteit",
  "quiz.evt.conference": "Conferentie",
  "quiz.evt.lancement_produit": "Productlancering",
  "quiz.evt.autre": "Anders",
  "quiz.evt.placeholder": "Geef het type evenement op",
  "quiz.cat.Plage": "Strand","quiz.cat.Montagne": "Bergen","quiz.cat.Ville": "Stad","quiz.cat.Nature": "Natuur","quiz.cat.Désert": "Woestijn","quiz.cat.Tropical": "Tropisch","quiz.cat.Bord de lac": "Aan het meer","quiz.cat.Île": "Eiland","quiz.cat.Historique": "Historisch","quiz.cat.Luxe": "Luxe","quiz.cat.Aventure": "Avontuur","quiz.cat.Bien-être": "Wellness",
  "quiz.dates.placeholder": "Kies een datum",
  "quiz.dates.or_month": "Of kies een maand",
  "quiz.participants.hint": "Aantal deelnemers",
  "quiz.participants.placeholder": "Bijv. 25",
  "quiz.budget.per_person": "ofwel € {p} per persoon",
  "quiz.budget.or": "OF",
  "quiz.budget.placeholder": "Exact totaal budget",
  "quiz.budget.exact": "Voer een exact bedrag in",
  "quiz.standing.Essentiel": "Essentieel","quiz.standing.Confort": "Comfort","quiz.standing.Premium": "Premium","quiz.standing.Prestige": "Prestige","quiz.standing.Exception": "Uitzonderlijk",
  "quiz.match.title": "Ontvang uw suggesties",
  "quiz.match.subtitle": "Voer uw zakelijke e-mail in om uw persoonlijke selectie te ontvangen.",
  "quiz.match.email_placeholder": "uw.email@dexcom.com",
  "quiz.match.cta": "Mijn suggesties ontvangen",
  "quiz.match.sending": "Versturen…",
  "quiz.match_suffix": "% match",
  "quiz.email.required": "E-mail vereist",
  "quiz.email.invalid": "Ongeldige e-mail",
  "quiz.email.pro": "Gebruik uw zakelijke e-mailadres",
  "quiz.success.title": "Aanvraag verzonden!",
  "quiz.success.subtitle": "Eugénie neemt zeer binnenkort contact met u op met een persoonlijke selectie.",
  "quiz.success.close": "Sluiten",
  "quiz.nav.back": "Terug",
  "quiz.nav.next": "Volgende",
  "quiz.aria.close": "Sluiten",
  "quiz.tier.Petit comité": "Kleine groep",
  "quiz.tier.Groupe convivial": "Gezellige groep",
  "quiz.tier.Grande équipe": "Groot team",
  "quiz.tier.Grand événement": "Groot evenement",
  "quiz.tier.Événement XL": "XL-evenement",
};

const de: Dict = {
  ...en,
  "header.discuter": "Chatten",
  "header.discuter_aria": "Mit Eugénie chatten",
  "header.demande_short": "Anfrage",
  "header.envoyer_demande": "Anfrage senden",
  "header.plateforme_short": "Plattform",
  "header.plateforme_full": "Zur DEXCOM-Plattform",
  "header.pill_role": ", Ihre Beraterin ",
  "header.pill_role_suffix": "",

  "seo.title": "Naboo × DEXCOM — Event-Plattform für DEXCOM-Mitarbeiter",
  "seo.description": "Naboo-Plattform exklusiv für DEXCOM-Mitarbeiter: organisieren Sie Seminare, Events und Teambuilding mit verhandelten Konditionen und persönlicher Betreuung durch Eugénie.",
  "seo.og.description": "Privater DEXCOM × Naboo-Bereich: organisieren Sie Firmenevents mit verhandelten Tarifen und persönlicher Betreuung.",
  "seo.og.image_alt": "DEXCOM- und Naboo-Logos — Ihre Event-Plattform",
  "seo.twitter.title": "Naboo × DEXCOM — Event-Plattform",
  "seo.twitter.description": "Privater DEXCOM × Naboo-Bereich: Seminare, Events und Teambuilding mit verhandelten Tarifen.",
  "seo.twitter.image_alt": "Naboo × DEXCOM — Event-Plattform",
  "seo.author": "Naboo für DEXCOM",
  "seo.locale": "de_DE",
  "html.lang": "de",

  "hero.title": "Ein paar schnelle Fragen, um Ihre ideale Location zu finden.",
  "hero.step.type": "Eventtyp",
  "hero.step.dates": "Daten",
  "hero.step.participants": "Teilnehmende",
  "hero.step.match": "Ihr Match",
  "hero.cta": "Starten →",
  "hero.subline": "Ergebnisse in unter 30 Sek.",

  "stats.partnership_value": "2025",
  "stats.partnership": "Partner seit",
  "stats.events": "Veranstaltungen",
  "stats.participants": "Teilnehmende",
  "stats.colleagues": "Registrierte Mitarbeitende",

  "colleagues.title": "Bereits 27 DEXCOM-Mitarbeitende auf Naboo registriert",
  "colleagues.paragraph": "Finden Sie die Historie Ihrer Events, den Stand Ihrer laufenden Projekte und alle Ihre Angebote an einem Ort.",
  "colleagues.cta": "Konto erstellen →",
  "colleagues.label": "Diese Mitarbeitenden nutzen Naboo bereits",
  "colleagues.more": "Mitarbeitende",

  "destinations.title": "Top-Reiseziele",
  "destinations.instant": "Sofortbuchung",
  "destinations.bottom_title": "Entdecken Sie über 50000 Locations und Dienstleister in unserer Datenbank",
  "destinations.bottom_subtitle": "Erkunden Sie den gesamten Naboo-Katalog für die DEXCOM-Teams.",
  "destinations.bottom_cta": "Locations entdecken →",

  "push.line1": "Ihre ideale Location in 30 Sek.",
  "push.line2": "Naboo Quiz",
  "push.close": "Schließen",

  "events.title": "Organisieren Sie jede Art von Event, von 5 bis 500 Teilnehmenden, weltweit",
  "events.stay": "Aufenthalt mit Übernachtung",
  "events.conf": "Konferenzen",
  "events.rooms": "Messestände",
  "events.awayday": "Auswärtstag | Meeting | Training",
  "events.parties": "Firmenfeiern | Afterworks",
  "events.meals": "Geschäftsessen | Catering",
  "events.tb": "Teambuiliding-Aktivitäten",

  "services.title": "Eine Plattform für Ihre gesamte Logistik",
  "services.paragraph": "Vom Konferenzraum bis zum Transferbus — wir kümmern uns um alles. Sie haben nur eine Ansprechpartnerin: Eugénie.",
  "services.catering": "Catering & Restaurants",
  "services.animation": "Speaker & Animation",
  "services.transport": "Transport & Transfers",
  "services.hotels": "Hotels & Locations",
  "services.coworking": "Meetingräume & Coworking",
  "services.more": "+ jede weitere Eventdienstleistung auf Anfrage",

  "pt.title": "Profitieren Sie von bevorzugten Konditionen",
  "pt.col.invoicing": "Rechnungsträgerdienst",
  "pt.col.standard": "Standardservice",
  "pt.col.turnkey": "Schlüsselfertig-Service",
  "pt.group.admin": "Verwaltung",
  "pt.group.search": "Veranstaltungsortsuche",
  "pt.group.management": "Veranstaltungsmanagement",
  "pt.row.fees": "Gebühren",
  "pt.row.advance": "Anzahlung",
  "pt.row.referencing": "Listing neuer Veranstaltungsorte und Dienstleister über Naboo",
  "pt.row.autonomy": "Volle Autonomie auf der Plattform",
  "pt.row.delegation": "Briefing-Delegation an Event-Experten mit unbegrenzten Vorschlägen und Unterstützung",
  "pt.row.assistance": "Unbegrenzte Unterstützung bei der Verhandlung von Preisen und Geschäftsbedingungen",
  "pt.row.app": "Event-App für Organisatoren und Teilnehmende",
  "pt.row.visit": "Besichtigung des Veranstaltungsorts",
  "pt.row.coordination": "Koordination der Dienstleister nach Unterzeichnung des Angebots",
  "pt.row.onsite": "Event-Manager vor Ort während der Veranstaltung (>40K€)",
  "pt.discover_fees": "Entdecken Sie unsere Gebühren",
  "pt.cta": "DEXCOM-Plattform entdecken",

  "how.badge": "In 6 einfachen Schritten",
  "how.title": "Wie funktioniert es?",
  "how.step": "Schritt",
  "how.prev": "Vorheriger Schritt",
  "how.next": "Nächster Schritt",
  "how.go_to": "Zu Schritt gehen",
  "how.s1.title": "Senden Sie Ihr Briefing",
  "how.s1.desc": "Senden Sie Ihr Briefing an Ihren Event-Experten (über die Plattform, E-Mail oder Telefon) oder suchen Sie mit der Suchleiste in voller Autonomie.",
  "how.s1.link.platform": "Link zur Plattform",
  "how.s2.title": "Erhalten Sie eine schnelle Antwort",
  "how.s2.desc": "Ihr Event-Experte antwortet innerhalb einer Stunde, macht erste Vorschläge innerhalb von 2 Stunden und stellt Ihre Angebote innerhalb von 2 Tagen fertig.",
  "how.s3.title": "Fordern Sie Anpassungen an",
  "how.s3.desc": "Fordern Sie Anpassungen oder neue Vorschläge an. Ihr Experte aktualisiert alles innerhalb von 24 Stunden, bis Sie bereit sind, Ihre Buchung zu bestätigen. Anpassungen sind unbegrenzt.",
  "how.s4.title": "Bestätigen Sie die Buchung",
  "how.s4.desc": "Während Ihre Kaufanfrage validiert wird, klicken Sie auf 'Buchung bestätigen' auf der Plattform, um die endgültige Rechnung herunterzuladen und Ihre Kaufanfrage in eine Bestellung umzuwandeln. Keine Sorgen wegen der Zahlung, Naboo kümmert sich um alles.",
  "how.s5.title": "Erstellen Sie Ihre Kaufanfrage",
  "how.s5.desc": "Wenn Sie mit Ihren Angeboten zufrieden sind, laden Sie das Angebot herunter, das Ihren Erwartungen entspricht, und starten Sie Ihren Kaufanfrageprozess.",
  "how.s6.title": "Erhalten Sie vollständige Unterstützung (optional)",
  "how.s6.desc": "Sollten Sie Hilfe bei der Organisation Ihrer Veranstaltung benötigen, kann Naboo Ihnen bis zum Tag der Veranstaltung einen vollständigen Event-Manager-Support bieten, fragen Sie einfach!",

  "footer.terms": "Allgemeine Nutzungsbedingungen",
  "footer.contact": "Kontaktieren Sie uns",
  "footer.cookies": "Cookie-Einstellungen ändern",

  "pop.subject": "DEXCOM-Anfrage",
  "pop.copy": "E-Mail kopieren",
  "pop.gmail": "In Gmail öffnen",
  "pop.outlook": "In Outlook öffnen",
  "pop.mail_app": "Meine E-Mail-App nutzen",
  "pop.copied": "E-Mail kopiert",
  "pop.copy_error": "Kopieren fehlgeschlagen",

  // Quiz DE
  "quiz.step.type": "Typ",
  "quiz.step.dates": "Daten",
  "quiz.step.categories": "Kategorien",
  "quiz.step.participants": "Teilnehmende",
  "quiz.step.standing": "Niveau",
  "quiz.step.match": "Match",
  "quiz.title.type": "Welche Art von Veranstaltung organisieren Sie?",
  "quiz.title.categories": "Welche Atmosphäre suchen Sie?",
  "quiz.title.participants": "Wie viele Teilnehmende?",
  "quiz.title.standing": "Wie hoch ist Ihr Budget?",
  "quiz.title.dates_prefix": "Wann findet Ihre ",
  "quiz.title.dates_suffix": " statt?",
  "quiz.title.event_word": "Veranstaltung",
  "quiz.label.0": "Veranstaltungstyp","quiz.label.1": "Daten","quiz.label.2": "Kategorien","quiz.label.3": "Teilnehmende","quiz.label.4": "Budget","quiz.label.5": "Ihre Daten",
  "quiz.month.0": "Januar","quiz.month.1": "Februar","quiz.month.2": "März","quiz.month.3": "April","quiz.month.4": "Mai","quiz.month.5": "Juni","quiz.month.6": "Juli","quiz.month.7": "August","quiz.month.8": "September","quiz.month.9": "Oktober","quiz.month.10": "November","quiz.month.11": "Dezember",
  "quiz.evt.seminaire": "Firmenseminar",
  "quiz.evt.journee_hors_site": "Offsite-Tag",
  "quiz.evt.soiree": "Firmenfeier",
  "quiz.evt.team_building": "Teambuiliding-Aktivitäten",
  "quiz.evt.conference": "Konferenz",
  "quiz.evt.lancement_produit": "Produkteinführung",
  "quiz.evt.autre": "Andere",
  "quiz.evt.placeholder": "Veranstaltungstyp angeben",
  "quiz.cat.Plage": "Strand","quiz.cat.Montagne": "Berge","quiz.cat.Ville": "Stadt","quiz.cat.Nature": "Natur","quiz.cat.Désert": "Wüste","quiz.cat.Tropical": "Tropisch","quiz.cat.Bord de lac": "Am See","quiz.cat.Île": "Insel","quiz.cat.Historique": "Historisch","quiz.cat.Luxe": "Luxus","quiz.cat.Aventure": "Abenteuer","quiz.cat.Bien-être": "Wellness",
  "quiz.dates.placeholder": "Datum auswählen",
  "quiz.dates.or_month": "Oder einen Monat wählen",
  "quiz.participants.hint": "Anzahl der Teilnehmenden",
  "quiz.participants.placeholder": "Z. B. 25",
  "quiz.budget.per_person": "d. h. {p} € pro Person",
  "quiz.budget.or": "ODER",
  "quiz.budget.placeholder": "Exaktes Gesamtbudget",
  "quiz.budget.exact": "Genauen Betrag eingeben",
  "quiz.standing.Essentiel": "Essentiell","quiz.standing.Confort": "Komfort","quiz.standing.Premium": "Premium","quiz.standing.Prestige": "Prestige","quiz.standing.Exception": "Außergewöhnlich",
  "quiz.match.title": "Erhalten Sie Ihre Vorschläge",
  "quiz.match.subtitle": "Geben Sie Ihre geschäftliche E-Mail-Adresse ein, um Ihre persönliche Auswahl zu erhalten.",
  "quiz.match.email_placeholder": "ihre.email@dexcom.com",
  "quiz.match.cta": "Meine Vorschläge erhalten",
  "quiz.match.sending": "Wird gesendet…",
  "quiz.match_suffix": "% Match",
  "quiz.email.required": "E-Mail erforderlich",
  "quiz.email.invalid": "Ungültige E-Mail",
  "quiz.email.pro": "Bitte verwenden Sie Ihre geschäftliche E-Mail-Adresse",
  "quiz.success.title": "Anfrage gesendet!",
  "quiz.success.subtitle": "Eugénie meldet sich in Kürze mit einer persönlichen Auswahl bei Ihnen.",
  "quiz.success.close": "Schließen",
  "quiz.nav.back": "Zurück",
  "quiz.nav.next": "Weiter",
  "quiz.aria.close": "Schließen",
  "quiz.tier.Petit comité": "Kleine Gruppe",
  "quiz.tier.Groupe convivial": "Gemütliche Gruppe",
  "quiz.tier.Grande équipe": "Großes Team",
  "quiz.tier.Grand événement": "Große Veranstaltung",
  "quiz.tier.Événement XL": "XL-Veranstaltung",
};

const dicts: Record<Lang, Dict> = { fr, es, nl, de, en };

export function detectLang(pathname: string): Lang {
  if (pathname.startsWith("/es")) return "es";
  if (pathname.startsWith("/nl")) return "nl";
  if (pathname.startsWith("/de")) return "de";
  if (pathname.startsWith("/en")) return "en";
  if (pathname.startsWith("/fr")) return "fr";
  return "en";
}

export function useArkemaLang() {
  const { pathname, search } = useLocation();
  const lang = detectLang(pathname);
  const t = (key: string): string => dicts[lang][key] ?? dicts.en[key] ?? key;
  const switchPath = (target: Lang): string => `/${target}${search || ""}`;
  return { lang, t, switchPath };
}

export function getT(lang: Lang) {
  return (key: string): string => dicts[lang][key] ?? dicts.en[key] ?? key;
}
